// Backend de produção: Supabase (Postgres + RLS + funções do timer + Edge Functions).
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Pricing, Subscription } from '../domain/access';
import { normalizeTime } from '../domain/dates';
import type { RecurringTask } from '../domain/recurring';
import type { Profile } from '../domain/settings';
import type { Task } from '../domain/task';
import type { AuthService } from './auth';
import { BackendError, type Backend, type BackendErrorCode } from './repository';
import { browserStorage, storedAdIds } from '../platform/adAttribution';

type PgError = { code?: string; message?: string } | null;

function fail(error: PgError | Error | unknown): never {
  const e = error as { code?: string; message?: string; name?: string };
  const msg = (e?.message ?? '').toLowerCase();
  let code: BackendErrorCode = 'unknown';
  if (e?.name === 'TypeError' || msg.includes('fetch') || msg.includes('network')) code = 'network';
  else if (e?.code === '42501' || msg.includes('no_access') || msg.includes('row-level security')) code = 'no_access';
  else if (e?.code === 'P0002' || e?.code === 'PGRST116') code = 'not_found';
  else if (e?.code === '23514' || e?.code === '22023' || e?.code === '22P02') code = 'invalid';
  throw new BackendError(code, e?.message);
}

function normalizeTask(row: Task): Task {
  return { ...row, due_time: row.due_time ? normalizeTime(row.due_time) : null };
}

function normalizeRecurring(row: RecurringTask): RecurringTask {
  return { ...row, due_time: row.due_time ? normalizeTime(row.due_time) : null };
}

/**
 * `from` diz ao servidor de onde vem o pedido do Checkout e do Portal do Stripe: no desktop, o
 * Stripe devolve para as páginas do site que reabrem o app; na web, para o próprio app (AC-112).
 */
export function createSupabaseBackend(client: SupabaseClient, auth: AuthService, from: 'desktop' | 'web' = 'web'): Backend {
  const tasks = () => client.from('tasks');
  const guard = async <T>(p: PromiseLike<{ data: T | null; error: PgError }>): Promise<T> => {
    let res: { data: T | null; error: PgError };
    try {
      res = await p;
    } catch (err) {
      fail(err);
    }
    if (res.error) fail(res.error);
    return res.data as T;
  };

  const invokeUrl = async (fn: string, body?: Record<string, unknown>): Promise<string> => {
    const { data, error } = await client.functions.invoke<{ url: string }>(fn, { body });
    if (error || !data?.url) fail(error ?? new Error('sem url'));
    return data.url;
  };

  return {
    mode: 'supabase',
    auth,
    tasks: {
      async list() {
        const rows = await guard<Task[]>(tasks().select('*').order('created_at'));
        return rows.map(normalizeTask);
      },
      async changes(since) {
        const res = await guard<{ tasks: Task[]; deleted: string[]; cursor: string; full: boolean }>(
          client.rpc('tasks_changes', { p_since: since })
        );
        return { ...res, tasks: res.tasks.map(normalizeTask) };
      },
      async create(fields) {
        return normalizeTask(await guard<Task>(tasks().insert(fields).select().single()));
      },
      async update(id, fields) {
        return normalizeTask(await guard<Task>(tasks().update(fields).eq('id', id).select().single()));
      },
      async remove(id) {
        await guard(tasks().delete().eq('id', id));
      },
      async start(id) {
        const rows = await guard<Task[]>(client.rpc('task_start', { p_id: id }));
        return rows.map(normalizeTask);
      },
      async pause(id) {
        return normalizeTask(await guard<Task>(client.rpc('task_pause', { p_id: id })));
      },
      async finish(id) {
        return normalizeTask(await guard<Task>(client.rpc('task_finish', { p_id: id })));
      },
      async reorder(ids) {
        await guard(client.rpc('task_reorder', { p_ids: ids }));
      },
    },
    recurring: {
      async list() {
        const rows = await guard<RecurringTask[]>(client.from('recurring_tasks').select('*').order('created_at'));
        return rows.map(normalizeRecurring);
      },
      async create(fields) {
        return normalizeRecurring(await guard<RecurringTask>(client.from('recurring_tasks').insert(fields).select().single()));
      },
      async update(id, fields) {
        return normalizeRecurring(await guard<RecurringTask>(client.from('recurring_tasks').update(fields).eq('id', id).select().single()));
      },
      async remove(id) {
        await guard(client.from('recurring_tasks').delete().eq('id', id));
      },
      async sync(today) {
        const rows = await guard<Task[]>(client.rpc('recurring_sync', { p_today: today }));
        return rows.map(normalizeTask);
      },
      async clear(id, today) {
        await guard(client.rpc('recurring_clear', { p_id: id, p_today: today }));
      },
    },
    profile: {
      async get() {
        return guard<Profile>(client.from('profiles').select('*').single());
      },
      async update(patch) {
        const session = await auth.getSession();
        if (!session) throw new BackendError('unauthenticated');
        return guard<Profile>(client.from('profiles').update(patch).eq('id', session.user.id).select().single());
      },
    },
    subscription: {
      async get() {
        return guard<Subscription | null>(client.from('subscriptions').select('*').maybeSingle());
      },
      async prices(deviceCurrency) {
        const { data, error } = await client.functions.invoke<Pricing>('prices', { body: { deviceCurrency } });
        if (error || !data) fail(error ?? new Error('sem preços'));
        return data;
      },
      // identificadores do anúncio vindos do site, para o aviso de assinatura ao Meta (AC-250), e o
      // idioma do app, que escolhe a língua do e-mail com o link do download (AC-260)
      checkoutUrl: (plan) =>
        invokeUrl('create-checkout', { plan, from, ad: storedAdIds(browserStorage()), lang: (typeof document !== 'undefined' && document.documentElement.lang) || undefined }),
      portalUrl: () => invokeUrl('customer-portal', { from }),
    },
    async deleteAccount() {
      const { error } = await client.functions.invoke('delete-account', { body: {} });
      if (error) fail(error);
      await auth.signOut();
    },
    async ping() {
      try {
        const { error } = await client.rpc('server_now');
        return !error;
      } catch {
        return false;
      }
    },
    async serverNow() {
      const iso = await guard<string>(client.rpc('server_now'));
      return Date.parse(iso);
    },
  };
}
